import { useEffect, useState } from "react";
import axios from "axios";
import PageShell, { PageHeader } from "@/components/PageShell";
import Seo from "@/components/Seo";
import { Trash2, Rss, ExternalLink, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "nema_admin_basic";

const initialForm = { title: "", description: "", link: "", category: "News" };

const AdminAnnouncementsPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [creds, setCreds] = useState({ user: "", pass: "" });
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [signInError, setSignInError] = useState("");
  const [success, setSuccess] = useState("");

  const authHeader = token ? { Authorization: `Basic ${token}` } : {};

  const load = async (t = token) => {
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/announcements`, {
        headers: { Authorization: `Basic ${t}` },
      });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setToken("");
        setSignInError("Invalid username or password.");
      } else {
        setError("Could not load announcements.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const signIn = (e) => {
    e.preventDefault();
    const t = btoa(`${creds.user}:${creds.pass}`);
    sessionStorage.setItem(STORAGE_KEY, t);
    setToken(t);
    setCreds({ user: "", pass: "" });
  };

  const signOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
    setItems([]);
  };

  const create = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      await axios.post(`${API}/admin/announcements`, form, { headers: authHeader });
      setForm(initialForm);
      setSuccess("Announcement published and added to the RSS feed.");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not publish. Please check the fields.");
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`${API}/admin/announcements/${slug}`, { headers: authHeader });
      load();
    } catch {
      setError("Could not delete.");
    }
  };

  if (!token) {
    return (
      <PageShell>
        <Seo title="Admin — Nema TV" description="Nema TV admin console." path="/admin/announcements" noindex />
        <PageHeader eyebrow="Admin" title="Sign in to manage announcements." />
        <div className="max-w-md mx-auto px-6 md:px-10">
          <form
            onSubmit={signIn}
            data-testid="admin-signin-form"
            className="rounded-2xl border border-white/10 bg-[#0F0A19] p-6 flex flex-col gap-4"
          >
            <label className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-[0.2em] text-white/60">Username</span>
              <input
                data-testid="admin-user"
                required autoFocus
                value={creds.user}
                onChange={(e) => setCreds((c) => ({ ...c, user: e.target.value }))}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE]"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-[0.2em] text-white/60">Password</span>
              <input
                data-testid="admin-pass"
                required type="password"
                value={creds.pass}
                onChange={(e) => setCreds((c) => ({ ...c, pass: e.target.value }))}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE]"
              />
            </label>
            <button
              type="submit"
              data-testid="admin-signin"
              className="rounded-full bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] text-white px-6 py-3 text-sm font-semibold"
            >
              Sign in
            </button>
            {error && <div className="text-red-300 text-sm">{error}</div>}
          </form>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Seo title="Admin · Announcements — Nema TV" description="Manage announcements." path="/admin/announcements" noindex />
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/60 mb-4">
              <Rss className="w-3.5 h-3.5" /> Admin
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-extrabold">Announcements</h1>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/api/rss.xml"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-rss-link"
              className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm text-white/80 hover:bg-white/10"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View RSS
            </a>
            <button
              onClick={signOut}
              data-testid="admin-signout"
              className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm text-white/80 hover:bg-white/10"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <form onSubmit={create} data-testid="announcement-form" className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#0F0A19] p-6 flex flex-col gap-4 h-fit">
            <h3 className="font-display text-xl font-bold">Publish new</h3>
            <input required placeholder="Title" data-testid="announcement-title"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE]" />
            <textarea required rows={4} placeholder="Description" data-testid="announcement-description"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE] resize-none" />
            <input required placeholder="Link URL" data-testid="announcement-link"
              value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE]" />
            <input placeholder="Category" data-testid="announcement-category"
              value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE]" />
            <button type="submit" data-testid="announcement-submit"
              className="rounded-full bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] text-white px-6 py-3 text-sm font-semibold">
              Publish
            </button>
            {success && <div data-testid="announcement-success" className="text-[#a7f3f9] text-sm">{success}</div>}
            {error && <div data-testid="announcement-error" className="text-red-300 text-sm">{error}</div>}
          </form>

          <div className="lg:col-span-3">
            <h3 className="font-display text-xl font-bold mb-4">Live feed ({items.length})</h3>
            {loading && <div className="text-white/50 text-sm">Loading…</div>}
            <div className="space-y-3" data-testid="announcement-list">
              {items.map((it) => (
                <div key={it.slug} data-testid={`announcement-row-${it.slug}`}
                     className="rounded-2xl border border-white/10 bg-[#0F0A19] p-5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">
                      <span className="rounded bg-white/5 border border-white/10 px-2 py-0.5">{it.category}</span>
                      <span>{new Date(it.published_at).toLocaleString()}</span>
                    </div>
                    <div className="font-display text-lg text-white font-semibold truncate">{it.title}</div>
                    <div className="text-white/60 text-sm mt-1 line-clamp-2">{it.description}</div>
                    <a className="text-[#22D3EE] text-xs mt-2 inline-flex items-center gap-1" href={it.link} target="_blank" rel="noreferrer">
                      Open link <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <button
                    onClick={() => remove(it.slug)}
                    data-testid={`announcement-delete-${it.slug}`}
                    className="shrink-0 inline-flex items-center gap-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-200 px-3 py-1.5 text-xs hover:bg-red-500/20"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              ))}
              {!loading && items.length === 0 && (
                <div className="rounded-2xl border border-dashed border-white/15 p-8 text-white/50 text-sm">
                  No announcements yet — publish your first one.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default AdminAnnouncementsPage;
